import React, { useState, useRef } from 'react';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import { SERVER_URL } from '../../../config';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash, faUpload } from '@fortawesome/free-solid-svg-icons';

const Uploads = () => {
    const [selectedCategory, setSelectedCategory] = useState('');
    const [selectedFile, setSelectedFile] = useState(null);
    const [loading, setLoading] = useState(false);
    const fileInputRef = useRef(null); // to reset the input

    const exampleReports = {
        "Past Medical Report": "E.g., Injury history, recovery documents, X-rays",
        "Blood Report": "E.g., Hemoglobin levels, Vitamin D, Iron, CBC",
        "Other": "E.g., Prescription, ECG, MRI reports",
    };

    const handleUpload = async () => {
        if (!selectedCategory || !selectedFile) {
            toast.error("Please select a report type and choose a file.");
            return;
        }

        const formData = new FormData();
        formData.append('pdf', selectedFile);
        formData.append('category', selectedCategory);
        setLoading(true);

        try {
            const response = await axios.post(`${SERVER_URL}/api/v2/upload-report`, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                    'Authorization': `Bearer ${sessionStorage.getItem("player_token")}`
                },
            });

            if (response.status === 200) {
                toast.success("Report uploaded successfully!");

                // Reset all states
                setSelectedFile(null);
                setSelectedCategory('');

                // Reset the file input value
                if (fileInputRef.current) {
                    fileInputRef.current.value = '';
                }
            }
        } catch (error) {
            console.error("Upload Error:", error);
            toast.error("Failed to upload report. Try again.");
        } finally {
            setLoading(false);
        }
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            const isPDF = file.type === "application/pdf";
            const isUnder5MB = file.size <= 5 * 1024 * 1024;

            if (!isPDF) {
                toast.error("Only PDF files are allowed.");
                return;
            }

            if (!isUnder5MB) {
                 if (fileInputRef.current) {
                    fileInputRef.current.value = '';
                }
                toast.error("File size should not exceed 5MB.");
                return;
            }

            setSelectedFile(file);
        }
    };

    const handleRemoveFile = () => {
        setSelectedFile(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    return (
        <div className="bg-gradient-to-b from-white/20 to-white/0 p-[1px] rounded-xl mt-6">
            <div className="bg-[#0b0a12] p-6 rounded-xl text-white">
                <h2 className="text-xl font-bold mb-4 text-center">Upload Medical Reports</h2>

                {/* Category Selector */}
                <div className="mb-4">
                    <label className="block mb-2 text-sm font-medium">Select Report Type</label>
                    <select
                        value={selectedCategory}
                        onChange={(e) => setSelectedCategory(e.target.value)}
                        className="w-full p-2 bg-[#1a1a2e] text-white border border-white/20 rounded"
                    >
                        <option value="">-- Choose Report Type --</option>
                        <option value="Past Medical Report">Past Medical Report</option>
                        <option value="Blood Report">Blood Report</option>
                        <option value="Other">Other</option>
                    </select>
                    {selectedCategory && (
                        <p className="text-xs text-white/50 mt-1">{exampleReports[selectedCategory]}</p>
                    )}
                </div>

                {/* File Upload */}
                <div className="mb-4">
                    <label className="block mb-2 text-sm font-medium">Select File (PDF, max 5MB)</label>
                    <div className="flex items-center gap-2">
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept=".pdf"
                            onChange={handleFileChange}
                            className="text-sm text-white bg-[#1a1a2e] border border-white/20 rounded w-full p-2"
                        />
                        {selectedFile && (
                            <button
                                onClick={handleRemoveFile}
                                className="text-red-500 hover:text-red-700"
                                title="Remove file"
                            >
                                <FontAwesomeIcon icon={faTrash} />
                            </button>
                        )}
                    </div>
                    {selectedFile && (
                        <p className="text-xs text-green-400 mt-1">Selected: {selectedFile.name}</p>
                    )}
                </div>

                {/* Upload Button */}
                <button
                    onClick={handleUpload}
                    disabled={loading}
                    className="bg-[#A588FF] hover:bg-[#8f6efb] text-white py-2 px-4 rounded transition duration-200 w-full flex items-center justify-center gap-2"
                >
                    <FontAwesomeIcon icon={faUpload} />
                    {loading ? "Uploading..." : "Upload Report"}
                </button>
            </div>
        </div>
    );
};

export default Uploads;
